<template lang="pug">

  div
    div( v-if="loading" ) Loading, please wait...
    div( v-else-if="!rakutenNetworkId" )
      div( class="alert alert-warning" ) Rakuten network not found. Please contact an administrator.
    div( v-else )

      div( class="row d-flex align-items-center mb-3" )
        div( class="col-6" )
          strong Filter Advertisers by Name:
          input( class="form-control" placeholder="Filter Results" v-model="filterString" )
        div( class="col-6 text-right" )
          p Page {{ page }} of {{ lastPageNum }}
          span Per page:&nbsp;
          select( v-model="perPage" )
            option( value="25" ) 25
            option( value="50" ) 50
            option( value="100" ) 100
          | &nbsp;
          div( class="btn-group" )
            button( class="btn btn-white" v-bind:disabled="page === 1" v-on:click="() => page -= 1" ) Prev page
            button( class="btn btn-white" v-bind:disabled="page === lastPageNum" v-on:click="() => page += 1" ) Next page

      div( class="d-flex justify-content-between align-items-center mb-3" )
        div
          strong Showing Rakuten advertisers only
          span( v-if="needsReplacement.length" class="ml-3 badge badge-warning" ) {{ needsReplacement.length }} with invalid Prog IDs
        div( class="btn-group" )
          button( class="btn btn-primary" v-on:click="fetchAllRanMids" v-bind:disabled="fetchingAll || replacingAll || $apollo.queries.advertisers.loading" )
            span( v-if="fetchingAll" )
              i( class="fa fa-spinner fa-spin mr-2" )
              | Fetching All...
            span( v-else ) Fetch All RanMIDs
          button( class="btn btn-warning" v-on:click="fetchAndReplaceAll" v-bind:disabled="fetchingAll || replacingAll || !needsReplacement.length || $apollo.queries.advertisers.loading" )
            span( v-if="replacingAll" )
              i( class="fa fa-spinner fa-spin mr-2" )
              | Replacing All...
            span( v-else ) Fetch &amp; Replace Invalid IDs

      table( class="table table-striped" )
        thead
          tr
            th Advertiser Name
            th Domain
            th Current Prog ID
            th Extracted ranMID
            th FMTC
            th Audit
            th Actions
        tbody
          tr( v-if="$apollo.queries.advertisers.loading" )
            td( colspan="7" class="text-center" )
              i( class="fa fa-spinner fa-spin mr-2" )
              | Loading...
          template( v-else-if="!advertisers || !advertisers.items || !advertisers.items.length" )
            tr
              td( colspan="7" class="text-center" ) No advertisers found
          template( v-else )
            tr( v-for="advertiser in advertisers.items" v-bind:key="advertiser.id" )
              td {{ advertiser.name }}
              td {{ advertiser.domain }}
              td
                code( v-bind:class="{ 'text-danger': hasInvalidProgId(advertiser) }" ) {{ advertiser.advertiserId }}
                br( v-if="hasInvalidProgId(advertiser)" )
                small( v-if="hasInvalidProgId(advertiser)" class="text-danger" ) Needs replacement
              td
                template( v-if="!ranMidData[advertiser.id]" )
                  button( class="btn btn-sm btn-outline-primary" v-on:click="fetchRanMid(advertiser)" ) Fetch
                template( v-else-if="ranMidData[advertiser.id].loading" )
                  i( class="fa fa-spinner fa-spin" )
                template( v-else-if="ranMidData[advertiser.id].error" )
                  span( class="text-muted" ) {{ ranMidData[advertiser.id].error }}
                template( v-else-if="ranMidData[advertiser.id].ranMid === advertiser.advertiserId" )
                  span( class="badge badge-success" ) {{ ranMidData[advertiser.id].ranMid }}
                  br
                  small( class="text-success" ) Already matches
                template( v-else )
                  span( class="badge badge-warning" ) {{ ranMidData[advertiser.id].ranMid }}
              td
                a(
                  v-bind:href="fmtcUrl(advertiser.name)"
                  target="_blank"
                  class="btn btn-sm btn-outline-secondary"
                ) FMTC
              td
                template( v-if="ranMidData[advertiser.id] && ranMidData[advertiser.id].auditId" )
                  router-link(
                    v-bind:to="`/reporting/audits/${ranMidData[advertiser.id].auditId}/technical-report`"
                    target="_blank"
                    class="btn btn-sm btn-outline-info"
                  ) View Audit
                template( v-else )
                  | -
              td
                button(
                  class="btn btn-sm btn-primary"
                  v-bind:disabled="!ranMidData[advertiser.id] || !ranMidData[advertiser.id].ranMid || ranMidData[advertiser.id].ranMid === advertiser.advertiserId || updating[advertiser.id]"
                  v-on:click="updateAdvertiserId(advertiser)"
                )
                  span( v-if="updating[advertiser.id]" )
                    i( class="fa fa-spinner fa-spin mr-1" )
                    | Updating...
                  span( v-else ) Replace with ranMID

</template>

<script>
import Vue from "vue";
import gql from "graphql-tag";

export default Vue.extend({
  data() {
    return {
      loading: true,
      networks: [],
      rakutenNetworkId: null,
      filterString: "",
      page: 1,
      perPage: 25,
      ranMidData: {},
      fetchingAll: false,
      replacingAll: false,
      updating: {},
    };
  },

  apollo: {
    advertisers: {
      query: gql`
        query AdvertiserPaginated(
          $page: Float!
          $perPage: Float!
          $networkId: ID
          $filterString: String
        ) {
          advertiserMany(
            page: $page
            perPage: $perPage
            filter: { networkId: { equals: $networkId } }
            filterString: $filterString
          ) {
            count
            items {
              id
              advertiserId
              name
              domain
              network {
                id
                name
              }
            }
          }
        }
      `,
      variables() {
        return {
          page: Number(this.page),
          perPage: Number(this.perPage),
          filterString: this.filterString,
          networkId: this.rakutenNetworkId,
        };
      },
      update: (data) => data.advertiserMany,
      skip() {
        return !this.rakutenNetworkId;
      },
      debounce: 400,
    },
  },

  computed: {
    lastPageNum() {
      return this.advertisers
        ? Math.ceil(this.advertisers.count / this.perPage)
        : "...";
    },
    needsReplacement() {
      if (!this.advertisers || !this.advertisers.items) return [];
      return this.advertisers.items.filter((a) => this.hasInvalidProgId(a));
    },
  },

  watch: {
    page() {
      this.ranMidData = {};
    },
    filterString() {
      this.ranMidData = {};
    },
    perPage() {
      this.page = 1;
      this.ranMidData = {};
    },
  },

  methods: {
    fmtcUrl(name) {
      const encoded = name.replace(/\s+/g, "+");
      return `https://directory.fmtc.co/cp/program_directory/index/sch/${encoded}/net/2/opm/0/cntry/0/cat/0/unsmrch/0`;
    },

    hasInvalidProgId(advertiser) {
      return /^\d+\.\d+$/.test(advertiser.advertiserId);
    },

    extractRanMidFromUrl(url) {
      try {
        const parsed = new URL(url);
        for (const [key, value] of parsed.searchParams) {
          if (key.toLowerCase() === "ranmid" && value) return value;
        }
      } catch (e) {
        // invalid URL, skip
      }
      return null;
    },

    extractRanMidFromEntries(entries) {
      if (!Array.isArray(entries)) return null;
      for (const entry of entries) {
        if (entry.url) {
          const ranMid = this.extractRanMidFromUrl(entry.url);
          if (ranMid) return ranMid;
        }
      }
      return null;
    },

    extractRanMidFromJourney(journey) {
      if (!journey) return null;
      if (typeof journey === "string") {
        try {
          journey = JSON.parse(journey);
        } catch (e) {
          return null;
        }
      }
      if (!Array.isArray(journey)) return null;

      // First try normal entries with .url property
      const fromNormal = this.extractRanMidFromEntries(journey);
      if (fromNormal) return fromNormal;

      // Handle corrupted journeys wrapped in { unparseable: "<json string>" }
      for (const entry of journey) {
        if (entry.unparseable && typeof entry.unparseable === "string") {
          try {
            const parsed = JSON.parse(entry.unparseable);
            const fromUnparseable = this.extractRanMidFromEntries(parsed);
            if (fromUnparseable) return fromUnparseable;
          } catch (e) {
            // unparseable string couldn't be parsed, skip
          }
        }
      }

      return null;
    },

    async fetchRanMid(advertiser) {
      const key = advertiser.id;
      this.$set(this.ranMidData, key, {
        loading: true,
        ranMid: null,
        error: null,
        auditId: null,
      });

      try {
        const maxPages = 5;
        let ranMid = null;
        let usedAuditId = null;
        let firstMatchedAuditId = null;

        for (let page = 1; page <= maxPages; page++) {
          const {
            data: { auditsForBatch },
          } = await this.$apollo.query({
            query: gql`
              query BatchlessAudits(
                $perPage: Float!
                $page: Float!
                $queryString: String
                $onlyCompleted: Boolean
                $timeframe: String
              ) {
                auditsForBatch(
                  page: $page
                  perPage: $perPage
                  queryString: $queryString
                  onlyCompleted: $onlyCompleted
                  timeframe: $timeframe
                ) {
                  pageInfo {
                    hasNextPage
                  }
                  items {
                    id
                    advertiser {
                      id
                      advertiserId
                    }
                  }
                }
              }
            `,
            variables: {
              perPage: 10,
              page,
              queryString: advertiser.name,
              onlyCompleted: true,
              timeframe: "all",
            },
            fetchPolicy: "network-only",
          });

          if (!auditsForBatch || !auditsForBatch.items || !auditsForBatch.items.length) {
            break;
          }

          const matchedAudits = auditsForBatch.items.filter(
            (a) => a.advertiser && a.advertiser.id === advertiser.id
          );

          if (!firstMatchedAuditId && matchedAudits.length) {
            firstMatchedAuditId = matchedAudits[0].id;
          }

          for (const matchedAudit of matchedAudits) {
            const {
              data: { auditById: fullAudit },
            } = await this.$apollo.query({
              query: gql`
                query AuditById($auditId: ID!) {
                  auditById(id: $auditId) {
                    id
                    journey
                  }
                }
              `,
              variables: {
                auditId: matchedAudit.id,
              },
              fetchPolicy: "network-only",
            });

            ranMid = this.extractRanMidFromJourney(fullAudit.journey);
            if (ranMid) {
              usedAuditId = fullAudit.id;
              break;
            }
          }

          if (ranMid || !auditsForBatch.pageInfo.hasNextPage) {
            break;
          }
        }

        if (!firstMatchedAuditId) {
          this.$set(this.ranMidData, key, {
            loading: false,
            ranMid: null,
            error: "No matching audit",
            auditId: null,
          });
          return;
        }

        if (!ranMid) {
          this.$set(this.ranMidData, key, {
            loading: false,
            ranMid: null,
            error: "No ranMID found (checked up to 50 audits)",
            auditId: firstMatchedAuditId,
          });
          return;
        }

        this.$set(this.ranMidData, key, {
          loading: false,
          ranMid,
          error: null,
          auditId: usedAuditId,
        });
      } catch (e) {
        this.$set(this.ranMidData, key, {
          loading: false,
          ranMid: null,
          error: e.message,
          auditId: null,
        });
      }
    },

    async fetchAllRanMids() {
      if (!this.advertisers || !this.advertisers.items) return;
      this.fetchingAll = true;

      for (const advertiser of this.advertisers.items) {
        if (!this.ranMidData[advertiser.id] || !this.ranMidData[advertiser.id].ranMid) {
          await this.fetchRanMid(advertiser);
        }
      }

      this.fetchingAll = false;
    },

    async fetchAndReplaceAll() {
      if (!this.needsReplacement.length) return;
      if (
        !confirm(
          `This will fetch ranMIDs and replace Prog IDs for ${this.needsReplacement.length} advertiser(s) with invalid IDs (e.g. 492895.6). Continue?`
        )
      ) {
        return;
      }

      this.replacingAll = true;

      for (const advertiser of this.needsReplacement) {
        if (!this.ranMidData[advertiser.id] || !this.ranMidData[advertiser.id].ranMid) {
          await this.fetchRanMid(advertiser);
        }

        const data = this.ranMidData[advertiser.id];
        if (data && data.ranMid && data.ranMid !== advertiser.advertiserId) {
          this.$set(this.updating, advertiser.id, true);
          try {
            await this.$apollo.mutate({
              mutation: gql`
                mutation UpdateAdvertiser(
                  $advertiserId: ID!
                  $advertiser: AdvertiserUpdateInput!
                ) {
                  advertiserUpdateById(
                    id: $advertiserId
                    record: $advertiser
                  ) {
                    id
                  }
                }
              `,
              variables: {
                advertiserId: advertiser.id,
                advertiser: { advertiserId: data.ranMid },
              },
            });
            this.$store.dispatch("flashMessage", {
              type: "success",
              msg: `Updated ${advertiser.name} Prog ID to ${data.ranMid}`,
            });
          } catch (e) {
            this.$store.dispatch("flashMessage", {
              type: "danger",
              msg: `Error updating ${advertiser.name}: ${e.message}`,
            });
          } finally {
            this.$set(this.updating, advertiser.id, false);
          }
        }
      }

      this.$apollo.queries.advertisers.refetch();
      this.replacingAll = false;
    },

    async updateAdvertiserId(advertiser) {
      const data = this.ranMidData[advertiser.id];
      if (!data || !data.ranMid) return;

      if (
        !confirm(
          `Replace Prog ID "${advertiser.advertiserId}" with ranMID "${data.ranMid}" for "${advertiser.name}"?`
        )
      ) {
        return;
      }

      this.$set(this.updating, advertiser.id, true);
      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation UpdateAdvertiser(
              $advertiserId: ID!
              $advertiser: AdvertiserUpdateInput!
            ) {
              advertiserUpdateById(
                id: $advertiserId
                record: $advertiser
              ) {
                id
              }
            }
          `,
          variables: {
            advertiserId: advertiser.id,
            advertiser: { advertiserId: data.ranMid },
          },
        });
        this.$store.dispatch("flashMessage", {
          type: "success",
          msg: `Updated ${advertiser.name} Prog ID to ${data.ranMid}`,
        });
        this.$apollo.queries.advertisers.refetch();
      } catch (e) {
        this.$store.dispatch("flashMessage", {
          type: "danger",
          msg: `Error updating ${advertiser.name}: ${e.message}`,
        });
      } finally {
        this.$set(this.updating, advertiser.id, false);
      }
    },
  },

  async beforeCreate() {
    this.networks = (await this.$moonpull.getNetworks()).filter(
      (n) => !n.aliasNetworkId
    );
    const rakuten = this.networks.find((n) => n.name === "Rakuten");
    if (rakuten) {
      this.rakutenNetworkId = rakuten.id;
    }
    this.loading = false;
  },
});
</script>
